import { useEffect, useMemo, useRef } from 'react'
import { Chart, BarController, BarElement, LinearScale, CategoryScale, Tooltip } from 'chart.js'
import { AppShell } from '../components/AppShell'
import { useBets } from '../lib/api'

Chart.register(BarController, BarElement, LinearScale, CategoryScale, Tooltip)

function fmtEur(v: string | number): string {
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)
}

export function StatsPage() {
  const bets = useBets()

  const sportPnl = useMemo(() => {
    const map = new Map<string, number>()
    for (const b of bets.data ?? []) {
      const k = b.sport ?? 'other'
      map.set(k, (map.get(k) ?? 0) + Number(b.pnl ?? 0))
    }
    return Array.from(map.entries())
  }, [bets.data])

  const stakeBuckets = useMemo(() => {
    const buckets = [0, 10, 25, 50, 100, 250, 500, 1000]
    const counts = new Array<number>(buckets.length).fill(0)
    for (const b of bets.data ?? []) {
      const s = Number(b.stake ?? 0)
      for (let i = buckets.length - 1; i >= 0; i--) {
        if (s >= (buckets[i] ?? 0)) { counts[i] = (counts[i] ?? 0) + 1; break }
      }
    }
    return buckets.map((b, i) => ({ label: `≥€${b}`, count: counts[i] ?? 0 }))
  }, [bets.data])

  const sportRef = useRef<HTMLCanvasElement | null>(null)
  const stakeRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const c = sportRef.current; if (!c) return
    const ctx = c.getContext('2d'); if (!ctx) return
    const chart = new Chart(ctx, {
      type: 'bar',
      data: { labels: sportPnl.map(([s]) => s),
        datasets: [{ data: sportPnl.map(([, v]) => v),
          backgroundColor: sportPnl.map(([, v]) => v >= 0 ? 'var(--color-success)' : 'var(--color-danger)'),
          borderRadius: 6 }] },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } },
        scales: { x: { grid: { display: false } }, y: { grid: { display: false } } } },
    })
    return () => chart.destroy()
  }, [sportPnl])

  useEffect(() => {
    const c = stakeRef.current; if (!c) return
    const ctx = c.getContext('2d'); if (!ctx) return
    const chart = new Chart(ctx, {
      type: 'bar',
      data: { labels: stakeBuckets.map((b) => b.label),
        datasets: [{ data: stakeBuckets.map((b) => b.count),
          backgroundColor: 'var(--color-primary)', borderRadius: 6 }] },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } },
        scales: { x: { grid: { display: false } }, y: { grid: { display: false } } } },
    })
    return () => chart.destroy()
  }, [stakeBuckets])

  const all = bets.data ?? []
  const wins   = all.filter((b) => b.status === 'won').length
  const losses = all.filter((b) => b.status === 'lost').length
  const net    = all.reduce((sum, b) => sum + Number(b.pnl ?? 0), 0)

  return (
    <AppShell page="bets" breadcrumb={<span className="topbar__breadcrumb-current">Statistics</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow"><span>Insights</span></p>
          <h1 className="page-header__title">Statistics</h1>
          <p className="page-header__subtitle">P&amp;L by sport and stake distribution.</p>
        </div>
      </header>

      <section className="kpi-grid" aria-label="Summary"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Total bets</span></header>
          <div className="kpi-card__value">{all.length}</div>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Wins</span></header>
          <div className="kpi-card__value">{wins}</div>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Losses</span></header>
          <div className="kpi-card__value">{losses}</div>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Net P&amp;L</span></header>
          <div className="kpi-card__value" style={{ color: net >= 0 ? 'var(--color-success-text)' : 'var(--color-danger-text)' }}>
            {net >= 0 ? '+' : ''}{fmtEur(net)}
          </div>
        </article>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)' }}>
        <section className="settings-card">
          <header className="settings-card__header">
            <h2 className="settings-card__title">P&amp;L by sport</h2>
            <p className="settings-card__description">Net profit per sport across all settled bets.</p>
          </header>
          <div className="settings-card__body" style={{ height: 280 }}>
            {sportPnl.length > 0 ? (
              <canvas ref={sportRef} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-tertiary)' }}>
                No data yet.
              </div>
            )}
          </div>
        </section>

        <section className="settings-card">
          <header className="settings-card__header">
            <h2 className="settings-card__title">Stake distribution</h2>
            <p className="settings-card__description">How your bet sizes spread across thresholds.</p>
          </header>
          <div className="settings-card__body" style={{ height: 280 }}>
            {all.length > 0 ? (
              <canvas ref={stakeRef} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-tertiary)' }}>
                No data yet.
              </div>
            )}
          </div>
        </section>
      </div>
    </AppShell>
  )
}
